import { CalendarIcon, Text } from '@fluentui/react-northstar';
import React, { useLayoutEffect, useRef } from 'react';
import EmailIcon from '../../../../svg/email.svg';
import PriorityIcon from '../../../../svg/priority-icon.svg';
import { appState } from '../../../AppState';
import { threedigitized } from '../../../tikit/ticketHelper';
import { ShortDateLabels } from '../../common/ConvertDate';
import { TicketStatus } from '../TicketCardTemplate';
import { UserAvatar } from '../UserAvatar';
import { dropToFit } from './dropToFit';
import { showParentChild } from './TicketDetailInfo';
import Styles from './TicketDetailCompact.module.css';

interface Props {
  ticketData: any;
}

const mutedStyle: React.CSSProperties = { color: '#8f90a6' };

/**
 * The phone's ticket card folded to two lines: number, requester and title, then status,
 * priority and when it came in. Only fields the full phone card already shows, shortened.
 *
 * The created time is shown only while the second line fits whole. A long status or
 * priority name pushes it off, and then the line falls back to the date alone rather than
 * cutting anything - measured, because both names are the tenant's own.
 */
export const TicketDetailCompact = ({ ticketData }: Props) => {
  const { priority } = appState();
  const lineRef = useRef<HTMLDivElement>(null);
  const priorityRef = useRef<HTMLSpanElement>(null);
  const withTimeRef = useRef<HTMLSpanElement>(null);
  const dateOnlyRef = useRef<HTMLSpanElement>(null);

  const priorityText = showParentChild(ticketData?.Priority, priority).join(' > ');
  const { dateOnly, withTime } = ShortDateLabels(ticketData?.CreatedDate);
  const isEmail = ticketData?.Source?.Value == 'Email';

  useLayoutEffect(() => {
    const line = lineRef.current;
    const withTimeEl = withTimeRef.current;
    const dateOnlyEl = dateOnlyRef.current;
    if (!line || !withTimeEl || !dateOnlyEl) return undefined;

    const fit = () => {
      const dropped = dropToFit(
        [withTimeEl],
        () => 1,
        el => { el.style.display = ''; dateOnlyEl.style.display = 'none'; },
        el => { el.style.display = 'none'; dateOnlyEl.style.display = ''; },
        () => {
          const prio = priorityRef.current;
          return line.scrollWidth > line.clientWidth || (!!prio && prio.scrollWidth > prio.clientWidth);
        }
      );
      return dropped;
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(line);
    return () => observer.disconnect();
  }, [ticketData, priorityText, withTime]);

  return (
    <div className={Styles.compact}>
      <div className={Styles.row}>
        <Text className={Styles.id}>#{threedigitized(ticketData?.Id)}</Text>
        {ticketData?.Requester != null && (
          <span className={Styles.avatar}>
            <UserAvatar
              idOrUpn={ticketData.Requester?.AadObjectId ?? ticketData.Requester?.UserName}
              avatarProps={{ name: ticketData.Requester?.FullName, size: 'smaller' }}
            />
          </span>
        )}
        <Text weight="semibold" className={Styles.title}>{ticketData?.Title}</Text>
      </div>
      <div ref={lineRef} className={Styles.row}>
        <span className={`${Styles.fixed} ${Styles.status} status-pill-cell`}>
          <TicketStatus status={ticketData?.Status} />
        </span>
        {priorityText && (
          <span className={Styles.priority}>
            <PriorityIcon width={20} height={20} className={`${Styles.icon} ${Styles.dot}`} />
            <span ref={priorityRef} className={`${Styles.truncate} ${Styles.text}`}>{priorityText}</span>
          </span>
        )}
        <span className={`${Styles.fixed} ${Styles.created}`}>
          <CalendarIcon size="small" style={mutedStyle} className={Styles.icon} />
          <span ref={withTimeRef} className={Styles.text}>{withTime}</span>
          <span ref={dateOnlyRef} className={Styles.text} style={{ display: 'none' }}>{dateOnly}</span>
          {isEmail && <EmailIcon width={12} height={12} stroke="#8f90a6" className={Styles.icon} />}
        </span>
      </div>
    </div>
  );
};
